import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Heart, MessageCircle, Sparkles, Bookmark, MapPin, Globe } from "lucide-react";
import { cn } from "@/lib/utils";

interface BoothMatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  matchedUserName: string;
  matchedUserAvatar?: string;
  boothName?: string;
  cityName?: string;
  onOpenPortal?: () => void;
  onContinueInBooth?: () => void;
  onSaveForLater?: () => void;
}

const BoothMatchModal: React.FC<BoothMatchModalProps> = ({
  isOpen,
  onClose,
  matchedUserName,
  matchedUserAvatar,
  boothName,
  cityName,
  onOpenPortal,
  onContinueInBooth,
  onSaveForLater,
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-sm text-center rounded-3xl border-0 bg-gradient-to-br from-background via-background to-pink-500/5 overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-32 h-32 bg-pink-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-1/4 w-40 h-40 bg-rose-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "0.5s" }} />
        </div>

        <div className="relative py-6">
          {/* Heart animation */}
          <div className="relative mx-auto w-24 h-24 mb-6">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-pink-400 via-rose-500 to-red-500 animate-pulse" />
            <div className="absolute inset-2 rounded-full bg-white dark:bg-gray-900 flex items-center justify-center">
              <Heart className="w-10 h-10 text-pink-500 fill-pink-500 animate-bounce" />
            </div>
            
            {/* Floating hearts */}
            <Heart className="absolute -top-2 -left-2 w-4 h-4 text-pink-400 fill-pink-400 animate-float" />
            <Heart className="absolute -top-1 -right-3 w-3 h-3 text-rose-400 fill-rose-400 animate-float" style={{ animationDelay: "0.3s" }} />
            <Heart className="absolute -bottom-1 -left-4 w-3 h-3 text-red-400 fill-red-400 animate-float" style={{ animationDelay: "0.6s" }} />
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-foreground mb-2">
            It's a Match! 💖
          </h2>
          <p className="text-muted-foreground mb-4">
            You and <span className="font-semibold text-foreground">{matchedUserName}</span> popped each other!
          </p>

          {/* Context badge */}
          {(boothName || cityName) && (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-pink-500/10 to-rose-500/10 border border-pink-500/20 mb-6">
              <Globe className="w-4 h-4 text-pink-500" />
              <span className="text-sm text-foreground">
                Met at <span className="font-semibold">{boothName || "Love Exhibition"}</span>
              </span>
              {cityName && (
                <>
                  <span className="text-pink-400">•</span>
                  <MapPin className="w-3 h-3 text-pink-500" />
                  <span className="text-sm">{cityName}</span>
                </>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-col gap-2">
            <Button
              onClick={onOpenPortal}
              className="w-full rounded-full bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Open Portal
            </Button>
            
            <Button
              variant="outline"
              onClick={onContinueInBooth}
              className="w-full rounded-full gap-2"
            >
              <MessageCircle className="w-4 h-4" />
              Continue in Booth
            </Button>
            
            <Button
              variant="ghost"
              onClick={onSaveForLater}
              className="w-full rounded-full gap-2 text-muted-foreground"
            >
              <Bookmark className="w-4 h-4" />
              Save for Follow-Up
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BoothMatchModal;
